/**
 * Extraction Quality — how much of the item definition Athena actually
 * understood from the uploaded documents (JLR gap E9-R1).
 *
 * When an item definition is seeded from a SysML diagram (Cameo/Rhapsody
 * exported as PNG/PDF) the vision pass is doing interpretation, not parsing.
 * A reviewer therefore has to know two things before trusting the result:
 * how confident each extraction was, and — more importantly — what the pass
 * could NOT classify.
 *
 * Anything unclassified is rendered explicitly. Silently dropping it would
 * leave a definition that looks complete while missing components that were
 * on the diagram, which is worse than showing a gap.
 *
 * All of this data already came back from the analysis agent; it simply had
 * no surface. Read-only: these are measurements of an extraction, not fields
 * a user edits.
 */
import React from 'react';
import StatusPill from '../../../../components/ui/StatusPill/StatusPill';
import { TypographyMuted, TypographySmall } from '../../../../components/ui/Typography';
import { SectionCard, SubSection, Field, ObjectListField } from './parts';
import type { SectionProps } from './parts';

/** Below this an extraction is a prompt to review, not a result to rely on. */
const LOW_CONFIDENCE = 0.6;

const COMPLETENESS_TONE: Record<string, 'success' | 'warning' | 'error'> = {
  high: 'success',
  medium: 'warning',
  low: 'error',
};

const SEVERITY_TONE: Record<string, 'error' | 'warning' | 'info'> = {
  critical: 'error',
  high: 'error',
  error: 'error',
  medium: 'warning',
  warning: 'warning',
  low: 'info',
};

const pct = (value: unknown): string =>
  typeof value === 'number' ? `${Math.round(value * 100)}%` : '—';

export function ConfidenceBadge({ value }: { value: unknown }) {
  if (typeof value !== 'number') return <TypographyMuted>—</TypographyMuted>;
  const tone = value >= 0.8 ? 'success' : value >= LOW_CONFIDENCE ? 'warning' : 'error';
  return <StatusPill tone={tone} label={pct(value)} size="sm" />;
}

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.filter(Boolean).map(String).join(', ') : v ? String(v) : '—';

export default function ExtractionQuality({ data, rawAgent }: SectionProps & { rawAgent?: any }) {
  const eq = data?.extraction_quality ?? {};
  const completeness = eq.completeness_assessment ?? {};
  const issues = Array.isArray(eq.extraction_issues) ? eq.extraction_issues : [];
  const documents = eq.documents_analyzed?.details ?? [];

  // Per-element confidence lives on the extracted records themselves. Surfacing
  // only the weak ones keeps this a review queue rather than a second copy of
  // the architecture table.
  const components = data?.system_architecture?.components?.list ?? [];
  const lowConfidence = (Array.isArray(components) ? components : []).filter(
    (c: any) => typeof c?.confidence === 'number' && c.confidence < LOW_CONFIDENCE,
  );

  return (
    <SectionCard id="extraction_quality">
      <SubSection title="Overall">
        <Field label="Extraction confidence" value={pct(eq.overall_confidence)} />
        <Field label="Documents analysed" value={eq.documents_analyzed?.count ?? 0} />
      </SubSection>

      <SubSection title="Completeness by category">
        <div>
          {Object.entries(completeness).map(([category, level]) => (
            <span key={category} style={{ display: 'inline-block', margin: '0 8px 8px 0' }}>
              <StatusPill
                tone={COMPLETENESS_TONE[String(level).toLowerCase()] ?? 'neutral'}
                label={`${category}: ${String(level)}`}
                size="sm"
              />
            </span>
          ))}
          {Object.keys(completeness).length === 0 && (
            <TypographyMuted>Not assessed.</TypographyMuted>
          )}
        </div>
      </SubSection>

      <SubSection title="Not classified">
        {issues.length === 0 ? (
          <TypographyMuted>
            Everything the extraction found was classified.
          </TypographyMuted>
        ) : (
          <>
            <TypographySmall>
              These were found in the source documents but could not be placed in the
              item definition. Review them before relying on the extraction.
            </TypographySmall>
            <ObjectListField
              rows={issues}
              columns={[
                {
                  key: 'severity',
                  label: 'Severity',
                  render: (v: unknown) => (
                    <StatusPill
                      tone={SEVERITY_TONE[String(v).toLowerCase()] ?? 'neutral'}
                      label={String(v ?? 'unknown')}
                      size="sm"
                    />
                  ),
                },
                { key: 'type', label: 'Type' },
                { key: 'message', label: 'Detail' },
                { key: 'affected_section', label: 'Where' },
                { key: 'recommendations', label: 'Suggested action', render: joinList },
              ]}
            />
          </>
        )}
      </SubSection>

      <SubSection title="Low-confidence components">
        {lowConfidence.length === 0 ? (
          <TypographyMuted>
            No component was extracted below {Math.round(LOW_CONFIDENCE * 100)}% confidence.
          </TypographyMuted>
        ) : (
          <ObjectListField
            rows={lowConfidence}
            columns={[
              { key: 'name', label: 'Component' },
              { key: 'type', label: 'Type' },
              {
                key: 'confidence',
                label: 'Confidence',
                render: (v: unknown) => <ConfidenceBadge value={v} />,
              },
              { key: 'source_documents', label: 'Source', render: joinList },
              { key: 'page_references', label: 'Page/section', render: joinList },
            ]}
          />
        )}
      </SubSection>

      {documents.length > 0 && (
        <SubSection title="Documents analysed">
          <ObjectListField
            rows={documents}
            columns={[
              { key: 'filename', label: 'File' },
              { key: 'detected_type', label: 'Detected as' },
              { key: 'pages_processed', label: 'Pages' },
              { key: 'extraction_status', label: 'Status' },
              {
                key: 'quality_score',
                label: 'Quality',
                render: (v: unknown) => <ConfidenceBadge value={v} />,
              },
              { key: 'missing_sections', label: 'Missing sections', render: joinList },
            ]}
          />
        </SubSection>
      )}
    </SectionCard>
  );
}
